import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LogOut, Plus, Pencil, Trash2, Save, X,
  RefreshCw, Search, AlertCircle, CheckCircle2,
  Loader2, Factory, Package2, Calculator, FlaskConical,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { SPEC_TAG_CFG, MATERIAL_TYPE_COLOR } from './AdminRecipes';

// ── 运费计算核心函数 ────────────────────────────────────
function calcFreightPerKg(
  base: number,
  firstKg: number,
  extra: number,
  qtyKg: number,
): number {
  if (qtyKg <= 0) return 0;
  const total = base + Math.max(0, qtyKg - firstKg) * extra;
  return total / qtyKg;
}

// ── 类型定义 ───────────────────────────────────────────
interface BOMItem {
  id: string;
  material_code: string;
  material_name: string;
  material_name_en: string | null;
  raw_material_cost: number;
  processing_cost: number;
  rd_sampling_cost: number;
  min_price: number;         // 保本底价 (DB computed: raw + processing)
  freight_base: number;      // 起步价(元)
  freight_first_kg: number;  // 首重(kg)
  freight_extra: number;     // 续重价(元/kg)
  order_qty_kg: number;      // 订单重量(kg)，用于摊算每kg运费
  freight_cost: number;      // 运费/kg（可手动覆盖）
  discount: number;          // 折扣系数 (0~1)
  unit: string;
  notes: string | null;
  updated_at: string;
  // ── 实时引用配方库（JOIN product_recipes 拍平字段）──
  recipe_id: string | null;
  recipe_spec_temp_range: string | null;
  recipe_spec_acid_resistance: string | null;
  recipe_spec_alkali_resistance: string | null;
  recipe_spec_oil_resistance: string | null;
  recipe_spec_ozone_resistance: string | null;
  recipe_spec_compression_set: string | null;
  recipe_spec_tensile_strength: string | null;
  recipe_spec_elongation: string | null;
  recipe_spec_seawater_resistance: string | null;
  recipe_spec_compliance: string | null;
  recipe_spec_qa_req: string | null;
  recipe_spec_tolerance: string | null;
  recipe_spec_thickness: string | null;
  recipe_spec_hardness: string | null;
}

// EditForm 只包含可编辑字段，不包含由 JOIN 产生的只读 recipe_* 字段
type EditForm = {
  material_code: string;
  material_name: string;
  material_name_en: string | null;
  raw_material_cost: number;
  processing_cost: number;
  rd_sampling_cost: number;
  freight_base: number;
  freight_first_kg: number;
  freight_extra: number;
  order_qty_kg: number;
  freight_cost: number;
  discount: number;
  unit: string;
  notes: string | null;
  recipe_id?: string | null; // 导入配方时写入，手动新增时为 null
};

// ── 配方导入相关类型 ──────────────────────────────────────
interface RecipeOption {
  id: string;
  product_code: string;
  product_name: string;
  material_type: string;
  spec_thickness: string | null;
  spec_hardness: string | null;
  spec_tolerance: string | null;
  spec_qa_req: string | null;
  spec_compliance: string | null;
  spec_acid_resistance: string | null;
  spec_ozone_resistance: string | null;
  spec_alkali_resistance: string | null;
  spec_compression_set: string | null;
  spec_oil_resistance: string | null;
  spec_temp_range: string | null;
  spec_seawater_resistance: string | null;
  spec_tensile_strength: string | null;
  spec_elongation: string | null;
}

const EMPTY_FORM: EditForm = {
  material_code: '',
  material_name: '',
  material_name_en: '',
  raw_material_cost: 0,
  processing_cost: 0,
  rd_sampling_cost: 0,
  freight_base: 20,
  freight_first_kg: 10,
  freight_extra: 1.5,
  order_qty_kg: 100,
  freight_cost: 0,
  discount: 1.0,
  unit: 'kg',
  notes: '',
};

// ── 行内编辑表单 ──────────────────────────────────────
function EditRow({ item, onSave, onCancel, saving }: {
  item: EditForm;
  onSave: (form: EditForm) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<EditForm>(item);
  const [freightManual, setFreightManual] = useState(false);

  const set = (k: keyof EditForm, v: string | number) =>
    setForm(prev => ({ ...prev, [k]: v }));

  // 自动运费计算
  const autoFreight = calcFreightPerKg(
    Number(form.freight_base),
    Number(form.freight_first_kg),
    Number(form.freight_extra),
    Number(form.order_qty_kg),
  );

  // 当运输参数改变时，若非手动模式则更新freight_cost
  const updateFreightParam = (k: keyof EditForm, v: number) => {
    setForm(prev => {
      const next = { ...prev, [k]: v };
      if (!freightManual) {
        next.freight_cost = calcFreightPerKg(
          Number(k === 'freight_base' ? v : prev.freight_base),
          Number(k === 'freight_first_kg' ? v : prev.freight_first_kg),
          Number(k === 'freight_extra' ? v : prev.freight_extra),
          Number(k === 'order_qty_kg' ? v : prev.order_qty_kg),
        );
      }
      return next;
    });
  };

  const minPrice = (Number(form.raw_material_cost) || 0) + (Number(form.processing_cost) || 0);
  const factoryPrice = minPrice * 1.05;
  const finalPrice = minPrice * 1.35 * (Number(form.discount) || 1) + (Number(form.freight_cost) || 0);

  const inp = "w-full bg-white border border-[#D0D5E2] rounded-sm px-2 py-1 text-xs font-mono outline-none focus:border-primary text-[#1A2337]";

  return (
    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="border-b border-primary/20" style={{ background: 'rgba(26,35,55,0.04)' }}>
      {/* 型号 */}
      <td className="px-3 py-2">
        <input value={form.material_code} onChange={e => set('material_code', e.target.value)}
          placeholder="如 FKM-S" maxLength={20} className={inp} />
      </td>
      {/* 名称 */}
      <td className="px-3 py-2 hidden md:table-cell">
        <input value={form.material_name} onChange={e => set('material_name', e.target.value)}
          placeholder="中文名称" className={inp} />
        <input value={form.material_name_en ?? ''} onChange={e => set('material_name_en', e.target.value)}
          placeholder="English name" className={`${inp} mt-1 opacity-60`} />
      </td>
      {/* 保本底价输入区 */}
      <td className="px-3 py-2">
        <div className="space-y-1">
          <div className="flex gap-1 items-center">
            <span className="text-[10px] text-[#5A6780] w-10 shrink-0">生胶</span>
            <input type="number" min="0" step="0.5" value={form.raw_material_cost}
              onChange={e => set('raw_material_cost', parseFloat(e.target.value) || 0)}
              className={`${inp} flex-1`} />
          </div>
          <div className="flex gap-1 items-center">
            <span className="text-[10px] text-[#5A6780] w-10 shrink-0">加工</span>
            <input type="number" min="0" step="0.5" value={form.processing_cost}
              onChange={e => set('processing_cost', parseFloat(e.target.value) || 0)}
              className={`${inp} flex-1`} />
          </div>
          <div className="text-[10px] text-primary font-mono font-bold pt-0.5">
            底价×1.05 ¥{factoryPrice.toFixed(2)}/kg
          </div>
        </div>
      </td>
      {/* 运输参数 */}
      <td className="px-3 py-2 hidden lg:table-cell">
        <div className="space-y-1">
          <div className="flex gap-1 items-center">
            <span className="text-[10px] text-[#5A6780] w-12 shrink-0">起步价</span>
            <input type="number" min="0" step="1" value={form.freight_base}
              onChange={e => updateFreightParam('freight_base', parseFloat(e.target.value) || 0)}
              className={`${inp} flex-1`} />
            <span className="text-[10px] text-[#5A6780]">元</span>
          </div>
          <div className="flex gap-1 items-center">
            <span className="text-[10px] text-[#5A6780] w-12 shrink-0">首重</span>
            <input type="number" min="0" step="1" value={form.freight_first_kg}
              onChange={e => updateFreightParam('freight_first_kg', parseFloat(e.target.value) || 0)}
              className={`${inp} flex-1`} />
            <span className="text-[10px] text-[#5A6780]">kg</span>
          </div>
          <div className="flex gap-1 items-center">
            <span className="text-[10px] text-[#5A6780] w-12 shrink-0">续重</span>
            <input type="number" min="0" step="0.1" value={form.freight_extra}
              onChange={e => updateFreightParam('freight_extra', parseFloat(e.target.value) || 0)}
              className={`${inp} flex-1`} />
            <span className="text-[10px] text-[#5A6780]">/kg</span>
          </div>
          <div className="flex gap-1 items-center">
            <span className="text-[10px] text-[#5A6780] w-12 shrink-0">订单量</span>
            <input type="number" min="1" step="10" value={form.order_qty_kg}
              onChange={e => updateFreightParam('order_qty_kg', parseFloat(e.target.value) || 1)}
              className={`${inp} flex-1`} />
            <span className="text-[10px] text-[#5A6780]">kg</span>
          </div>
        </div>
      </td>
      {/* 运费/kg */}
      <td className="px-3 py-2">
        <input type="number" min="0" step="0.01" value={form.freight_cost}
          onChange={e => { setFreightManual(true); set('freight_cost', parseFloat(e.target.value) || 0); }}
          className={inp} />
        <div className="text-[10px] mt-0.5" style={{ color: freightManual ? '#F97316' : '#5A6780' }}>
          {freightManual
            ? `手动覆盖（自动值 ¥${autoFreight.toFixed(3)}）`
            : `自动预填，可手动修改`}
        </div>
        {freightManual && (
          <button onClick={() => { setFreightManual(false); set('freight_cost', autoFreight); }}
            className="text-[10px] text-primary hover:underline mt-0.5">
            ↺ 恢复自动
          </button>
        )}
      </td>
      {/* 折扣 */}
      <td className="px-3 py-2">
        <input type="number" min="0.1" max="1" step="0.05" value={form.discount}
          onChange={e => set('discount', parseFloat(e.target.value) || 1)}
          className={inp} />
        <div className="text-[10px] text-[#5A6780] mt-0.5">
          {((Number(form.discount) || 1) * 10).toFixed(1)}折
        </div>
      </td>
      {/* 最终报价（只读） */}
      <td className="px-3 py-2 text-center">
        <span className="text-sm font-black font-mono" style={{ color: 'oklch(0.66 0.19 42)' }}>
          ¥{finalPrice.toFixed(2)}
        </span>
        <div className="text-[10px] text-[#5A6780]">/kg</div>
      </td>
      {/* 操作 */}
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <button onClick={() => onSave(form)} disabled={saving || !form.material_code || !form.material_name}
            className="flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-sm bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          </button>
          <button onClick={onCancel} className="p-1 rounded-sm text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>
      </td>
    </motion.tr>
  );
}

// ── 内容核心组件（不含顶栏，可嵌入 Dashboard）────────────
function AdminBOMContent() {
  const [items, setItems] = useState<BOMItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  // 配方导入弹窗状态
  const [showRecipePicker, setShowRecipePicker] = useState(false);
  const [recipes, setRecipes] = useState<RecipeOption[]>([]);
  const [recipeSearch, setRecipeSearch] = useState('');
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [prefillForm, setPrefillForm] = useState<EditForm | null>(null);
  const [prefillRecipeId, setPrefillRecipeId] = useState<string | null>(null);

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2800);
  };

const fetchBOM = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true); else setLoading(true);
    const { data, error } = await supabase
      .from('materials_bom')
      .select(`
        *,
        product_recipes (
          spec_temp_range, spec_acid_resistance, spec_alkali_resistance,
          spec_oil_resistance, spec_ozone_resistance, spec_compression_set,
          spec_tensile_strength, spec_elongation, spec_seawater_resistance,
          spec_compliance, spec_qa_req, spec_tolerance,
          spec_thickness, spec_hardness
        )
      `)
      .order('material_code');
    if (!error && data) {
      // 拍平 JOIN 数据：把 product_recipes 嵌套对象展开为 recipe_* 前缀字段
      const flat = (data as unknown as Array<Record<string, unknown>>).map(row => {
        const pr = row.product_recipes as Record<string, string | null> | null;
        return {
          ...row,
          recipe_spec_temp_range:          pr?.spec_temp_range ?? null,
          recipe_spec_acid_resistance:     pr?.spec_acid_resistance ?? null,
          recipe_spec_alkali_resistance:   pr?.spec_alkali_resistance ?? null,
          recipe_spec_oil_resistance:      pr?.spec_oil_resistance ?? null,
          recipe_spec_ozone_resistance:    pr?.spec_ozone_resistance ?? null,
          recipe_spec_compression_set:     pr?.spec_compression_set ?? null,
          recipe_spec_tensile_strength:    pr?.spec_tensile_strength ?? null,
          recipe_spec_elongation:          pr?.spec_elongation ?? null,
          recipe_spec_seawater_resistance: pr?.spec_seawater_resistance ?? null,
          recipe_spec_compliance:          pr?.spec_compliance ?? null,
          recipe_spec_qa_req:              pr?.spec_qa_req ?? null,
          recipe_spec_tolerance:           pr?.spec_tolerance ?? null,
          recipe_spec_thickness:           pr?.spec_thickness ?? null,
          recipe_spec_hardness:            pr?.spec_hardness ?? null,
          product_recipes: undefined as unknown as null, // 不保留嵌套对象
        };
      });
      setItems(flat as unknown as BOMItem[]);
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    fetchBOM();
  }, [fetchBOM]);

// ── 拉取所有配方 ──
  const fetchRecipes = useCallback(async () => {
    setLoadingRecipes(true);
    const { data } = await supabase
      .from('product_recipes')
      .select('id,product_code,product_name,material_type,spec_thickness,spec_hardness,spec_tolerance,spec_qa_req,spec_compliance,spec_acid_resistance,spec_ozone_resistance,spec_alkali_resistance,spec_compression_set,spec_oil_resistance,spec_temp_range,spec_seawater_resistance,spec_tensile_strength,spec_elongation')
      .eq('is_active', true)
      .order('product_code');
    setRecipes((data ?? []) as RecipeOption[]);
    setLoadingRecipes(false);
  }, []);

  const openRecipePicker = () => {
    setRecipeSearch('');
    setShowRecipePicker(true);
    fetchRecipes();
  };

  // ── 用户选择某个配方后：拉取原料+工艺，计算建议报价，预填表单 ──
  const handleImportRecipe = async (recipe: RecipeOption) => {
    setImportingId(recipe.id);
    const [{ data: rms }, { data: rrs }] = await Promise.all([
      supabase.from('recipe_materials').select('ratio_phr, raw_materials(unit_price)').eq('recipe_id', recipe.id),
      supabase.from('recipe_routings').select('override_cost, process_routings(cost_per_kg)').eq('recipe_id', recipe.id),
    ]);

    // 计算原料成本/kg
    const mats = (rms ?? []) as unknown as Array<{ ratio_phr: number; raw_materials: { unit_price: number } | null }>;
    const totalPhr = mats.reduce((s, r) => s + Number(r.ratio_phr), 0);
    const matCost = totalPhr > 0
      ? mats.reduce((s, r) => s + (Number(r.ratio_phr) / totalPhr) * Number(r.raw_materials?.unit_price ?? 0), 0)
      : 0;

    // 计算工艺加工费/kg
    const rts = (rrs ?? []) as unknown as Array<{ override_cost: number | null; process_routings: { cost_per_kg: number } | null }>;
    const processCost = rts.reduce((s, r) => {
      const c = r.override_cost != null ? Number(r.override_cost) : Number(r.process_routings?.cost_per_kg ?? 0);
      return s + c;
    }, 0);

    // 综合成本拆分填入：原料成本 + 加工成本，min_price = raw + processing
    // 最终报价 = min_price × 1.35 × discount + freight（建议售价逻辑）
    // 出厂底价 = min_price × 1.05（成本加价5%）
// 不再拼接 notes 字符串快照，改为存 recipe_id，标签实时从 JOIN 读取
    const form: EditForm = {
      ...EMPTY_FORM,
      material_code: recipe.product_code,
      material_name: recipe.product_name,
      material_name_en: recipe.material_type,
      raw_material_cost: parseFloat(matCost.toFixed(2)),
      processing_cost: parseFloat(processCost.toFixed(2)),
      notes: null, // 不再用 notes 存标签快照
    };

    setImportingId(null);
    setShowRecipePicker(false);
    setPrefillForm(form);
    setPrefillRecipeId(recipe.id); // 记录 recipe_id，保存时写入
    setEditingId('new');
  };

// 保存（新增 or 更新）
  const handleSave = async (form: EditForm, id?: string, recipeId?: string | null) => {
    setSavingId(id ?? 'new');
    const { data: { user } } = await supabase.auth.getUser();

    const freightCost = form.freight_cost > 0
      ? Number(form.freight_cost)
      : calcFreightPerKg(
          Number(form.freight_base),
          Number(form.freight_first_kg),
          Number(form.freight_extra),
          Number(form.order_qty_kg),
        );

    const payload = {
      material_code: form.material_code.trim().toUpperCase(),
      material_name: form.material_name.trim(),
      material_name_en: form.material_name_en?.trim() || null,
      raw_material_cost: Number(form.raw_material_cost),
      processing_cost: Number(form.processing_cost),
      rd_sampling_cost: Number(form.rd_sampling_cost),
      freight_base: Number(form.freight_base),
      freight_first_kg: Number(form.freight_first_kg),
      freight_extra: Number(form.freight_extra),
      order_qty_kg: Number(form.order_qty_kg),
      freight_cost: freightCost,
      discount: Number(form.discount),
      unit: form.unit,
      notes: form.notes?.trim() || null,
      updated_by: user?.id,
    };

let error;
    if (id) {
      ({ error } = await supabase.from('materials_bom').update(payload).eq('id', id));
    } else {
      ({ error } = await supabase.from('materials_bom').insert({ ...payload, recipe_id: recipeId ?? null }));
    }
    setSavingId(null);

    if (error) {
      showToast('error', error.message.includes('unique') ? '型号编码已存在，请修改' : '保存失败：' + error.message);
} else {
      showToast('success', id ? '已更新成功' : '新增成功');
      setEditingId(null);
      setPrefillRecipeId(null); // 保存后清空
      fetchBOM(true);
    }
  };

  // 删除
  const handleDelete = async (id: string) => {
    setDeleteId(null);
    const { error } = await supabase.from('materials_bom').delete().eq('id', id);
    if (error) showToast('error', '删除失败：' + error.message);
    else { showToast('success', '已删除'); setItems(prev => prev.filter(i => i.id !== id)); }
  };

  const filtered = items.filter(i => {
    const q = search.toLowerCase();
    return !q || [i.material_code, i.material_name, i.notes ?? ''].some(v => v.toLowerCase().includes(q));
  });

  // 展示用：最终报价
  const getDisplayFinalPrice = (item: BOMItem) => {
    const freight = item.freight_cost > 0
      ? item.freight_cost
      : calcFreightPerKg(item.freight_base ?? 20, item.freight_first_kg ?? 10, item.freight_extra ?? 1.5, item.order_qty_kg ?? 100);
    return item.min_price * 1.35 * (item.discount ?? 1) + freight;
  };

  return (
    <div className="w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* 页头 */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Factory className="w-5 h-5 text-primary" />
            <span className="text-xs font-mono font-bold tracking-widest text-primary uppercase">工厂视图</span>
          </div>
          <h1 className="text-2xl font-bold">商务报价测算</h1>
          <p className="text-sm text-muted-foreground mt-1">
            维护各型号橡胶板的成本与运费参数，系统自动计算每千克最终报价。
            <span className="ml-2 font-mono text-xs text-primary/80">最终报价 = 综合成本×1.35 × 折扣 + 模具费/kg</span>
          </p>
        </div>

        {/* 计算说明卡片 */}
        <div className="rounded-sm border border-[#D0D5E2] bg-[#F7F8FA] p-4 flex flex-wrap gap-6 text-xs">
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-primary flex-shrink-0" />
            <div>
              <div className="text-[#1A2337] font-bold mb-0.5">零担运费公式</div>
              <div className="text-[#5A6780] font-mono">总运费 = 起步价 + MAX(0, 订单量 - 首重) × 续重价</div>
              <div className="text-[#5A6780] font-mono">运费/kg = 总运费 ÷ 订单量</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-primary flex-shrink-0" />
            <div>
              <div className="text-[#1A2337] font-bold mb-0.5">举例：100kg订单</div>
              <div className="text-[#5A6780] font-mono">起步20元/10kg首重，续重1.5元</div>
              <div className="text-[#5A6780] font-mono">= (20 + 90×1.5) ÷ 100 = <span className="text-primary">1.55元/kg</span></div>
            </div>
          </div>
        </div>

        {/* 操作栏 */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input type="search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="搜索型号、名称…"
              className="w-full pl-9 pr-4 py-2 rounded-sm text-sm bg-input border border-border outline-none focus:border-primary"
            />
          </div>
          <button onClick={() => fetchBOM(true)} disabled={refreshing}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded-sm px-3 py-2 transition-colors disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          {/* 从配方导入（主按钮） */}
          <button
            onClick={openRecipePicker}
            disabled={editingId !== null}
            className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-sm hover:opacity-90 disabled:opacity-50 transition-opacity"
            style={{ background: 'oklch(0.66 0.19 42)', color: '#fff' }}
          >
            <FlaskConical className="w-3.5 h-3.5" />从配方导入
          </button>
          {/* 手动新增（次要按钮） */}
          <button
            onClick={() => { setPrefillForm(null); setEditingId('new'); }}
            disabled={editingId !== null}
            className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-sm border border-border text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />手动新增
          </button>
        </div>

        {/* 主表格 */}
        {loading ? (
          <div className="card-industrial p-16 flex flex-col items-center gap-4 text-muted-foreground">
            <RefreshCw className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="card-industrial overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border" style={{ background: '#F0F2F5' }}>
                    {[
                      { label: '型号', cls: '' },
                      { label: '材料名称', cls: 'hidden md:table-cell' },
                      { label: '出厂底价', cls: '' },
                      { label: '运输参数', cls: 'hidden lg:table-cell' },
                      { label: '运费/kg', cls: '' },
                      { label: '折扣', cls: '' },
                      { label: '最终报价', cls: '' },
                      { label: '操作', cls: '' },
                    ].map(({ label, cls }) => (
                      <th key={label} className={`text-left text-xs font-mono font-bold tracking-widest text-muted-foreground uppercase px-3 py-3 whitespace-nowrap ${cls}`}>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* 新增行 */}
                  <AnimatePresence>
                {editingId === 'new' && (
                      <EditRow
                        item={prefillForm ?? EMPTY_FORM}
                        onSave={form => handleSave(form, undefined, prefillRecipeId)}
                        onCancel={() => { setEditingId(null); setPrefillForm(null); setPrefillRecipeId(null); }}
                        saving={savingId === 'new'}
                      />
                    )}
                  </AnimatePresence>

                  {filtered.length === 0 && editingId !== 'new' ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-muted-foreground">
                        <Package2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
                        <p>暂无 BOM 数据</p>
                      </td>
                    </tr>
                  ) : (
                    filtered.map(item => (
                      <AnimatePresence key={item.id}>
                        {editingId === item.id ? (
                          <EditRow
                            item={{
                              material_code: item.material_code,
                              material_name: item.material_name,
                              material_name_en: item.material_name_en,
                              raw_material_cost: item.raw_material_cost,
                              processing_cost: item.processing_cost,
                              rd_sampling_cost: item.rd_sampling_cost,
                              freight_base: item.freight_base ?? 20,
                              freight_first_kg: item.freight_first_kg ?? 10,
                              freight_extra: item.freight_extra ?? 1.5,
                              order_qty_kg: item.order_qty_kg ?? 100,
                              freight_cost: item.freight_cost ?? 0,
                              discount: item.discount ?? 1.0,
                              unit: item.unit,
                              notes: item.notes,
                            }}
                            onSave={form => handleSave(form, item.id)}
                            onCancel={() => setEditingId(null)}
                            saving={savingId === item.id}
                          />
                        ) : (
                          <motion.tr layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            className="border-b border-border hover:bg-muted/10 transition-colors">
                            {/* 型号 */}
                            <td className="px-3 py-3">
                              <span className="font-mono text-xs font-bold bg-primary/10 text-primary border border-primary/20 px-1.5 py-0.5 rounded-sm">
                                {item.material_code}
                              </span>
                            </td>
                            {/* 名称 */}
                            <td className="px-3 py-3 hidden md:table-cell">
                              <div className="font-medium text-sm">{item.material_name}</div>
                              {item.material_name_en && (
                                <div className="text-xs text-muted-foreground">{item.material_name_en}</div>
                              )}
                              {/* 小标签：直接从 JOIN 的 recipe_* 字段读取，与配方库完全实时同步，永不脱节 */}
                              {item.recipe_id && (() => {
                                const LIVE_SPEC_KEYS: Array<[keyof BOMItem, string]> = [
                                  ['recipe_spec_temp_range',          'spec_temp_range'],
                                  ['recipe_spec_acid_resistance',     'spec_acid_resistance'],
                                  ['recipe_spec_alkali_resistance',   'spec_alkali_resistance'],
                                  ['recipe_spec_oil_resistance',      'spec_oil_resistance'],
                                  ['recipe_spec_ozone_resistance',    'spec_ozone_resistance'],
                                  ['recipe_spec_compression_set',     'spec_compression_set'],
                                  ['recipe_spec_tensile_strength',    'spec_tensile_strength'],
                                  ['recipe_spec_elongation',          'spec_elongation'],
                                  ['recipe_spec_seawater_resistance', 'spec_seawater_resistance'],
                                  ['recipe_spec_compliance',          'spec_compliance'],
                                  ['recipe_spec_qa_req',              'spec_qa_req'],
                                  ['recipe_spec_tolerance',           'spec_tolerance'],
                                ];
                                const hasTags = item.recipe_spec_thickness || item.recipe_spec_hardness
                                  || LIVE_SPEC_KEYS.some(([k]) => !!(item[k] as string | null));
                                if (!hasTags) return null;
                                return (
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    {item.recipe_spec_thickness && (
                                      <span style={{ background: 'rgba(180,83,9,0.10)', color: '#B45309', border: '1px solid #B4530955', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 600 }}>
                                        密度: {item.recipe_spec_thickness}
                                      </span>
                                    )}
                                    {item.recipe_spec_hardness && (
                                      <span style={{ background: 'rgba(29,78,216,0.09)', color: '#1D4ED8', border: '1px solid #1D4ED855', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 600 }}>
                                        硬度: {item.recipe_spec_hardness}
                                      </span>
                                    )}
                                    {LIVE_SPEC_KEYS.map(([k, cfgKey]) => {
                                      const val = item[k] as string | null;
                                      if (!val) return null;
                                      const cfg = SPEC_TAG_CFG[cfgKey];
                                      return (
                                        <span key={k as string} style={{
                                          background: cfg?.bg ?? 'rgba(55,48,163,0.09)',
                                          color: cfg?.color ?? '#3730A3',
                                          border: `1px solid ${cfg?.color ? cfg.color + '55' : '#C7D2FE'}`,
                                          borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 600,
                                        }}>{cfg?.label ?? cfgKey}: {val}</span>
                                      );
                                    })}
                                  </div>
                                );
                              })()}
                            </td>
                            {/* 出厂底价 */}
                            <td className="px-3 py-3 whitespace-nowrap">
                              <span className="font-mono font-bold text-primary">¥{(item.min_price * 1.05).toFixed(2)}/kg</span>
                            </td>
                            {/* 运输参数摘要 */}
                            <td className="px-3 py-3 hidden lg:table-cell">
                              <div className="text-xs font-mono text-[#5A6780] space-y-0.5">
                                <div>起步 ¥{(item.freight_base ?? 20).toFixed(0)} / {(item.freight_first_kg ?? 10).toFixed(0)}kg首重</div>
                                <div>续重 ¥{(item.freight_extra ?? 1.5).toFixed(2)}/kg</div>
                                <div>按 {(item.order_qty_kg ?? 100).toFixed(0)}kg 摊算</div>
                              </div>
                            </td>
                            {/* 运费/kg */}
                            <td className="px-3 py-3 whitespace-nowrap">
                              <span className="font-mono text-sm text-[#1A2337]">
                                ¥{(item.freight_cost > 0
                                  ? item.freight_cost
                                  : calcFreightPerKg(item.freight_base ?? 20, item.freight_first_kg ?? 10, item.freight_extra ?? 1.5, item.order_qty_kg ?? 100)
                                ).toFixed(3)}
                              </span>
                            </td>
                            {/* 折扣 */}
                            <td className="px-3 py-3 whitespace-nowrap">
                              <span className="font-mono text-sm text-[#1A2337]">
                                {((item.discount ?? 1) * 10).toFixed(1)}折
                              </span>
                            </td>
                            {/* 最终报价 */}
                            <td className="px-3 py-3 whitespace-nowrap">
                              <span className="font-mono font-black text-base" style={{ color: 'oklch(0.66 0.19 42)' }}>
                                ¥{getDisplayFinalPrice(item).toFixed(2)}/kg
                              </span>
                            </td>
                            {/* 操作 */}
                            <td className="px-3 py-3">
                              <div className="flex items-center gap-1">
                                <button onClick={() => setEditingId(item.id)} disabled={editingId !== null}
                                  className="p-1.5 rounded-sm text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-30"
                                  title="编辑">
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => setDeleteId(item.id)} disabled={editingId !== null}
                                  className="p-1.5 rounded-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-30"
                                  title="删除">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        )}
                      </AnimatePresence>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-border bg-muted/10 text-xs text-muted-foreground flex flex-wrap items-center gap-4">
              <span>共 <span className="font-bold text-foreground">{items.length}</span> 个型号</span>
              <span className="font-mono text-primary/70">最终报价 = 综合成本×1.35 × 折扣 + 模具费/kg　|　出厂底价 = 综合成本×1.05</span>
              <span className="font-mono text-[#5A6780]">运费/kg = (起步价 + MAX(0, 订单量-首重) × 续重价) ÷ 订单量</span>
            </div>
          </div>
        )}
      </div>

      {/* ── 配方选择弹窗 ── */}
      <AnimatePresence>
        {showRecipePicker && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowRecipePicker(false)}
              className="absolute inset-0 bg-black/70"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              className="relative w-full max-w-2xl max-h-[80vh] flex flex-col rounded-sm shadow-2xl"
              style={{ background: '#FFFFFF', border: '1px solid #D0D5E2' }}
            >
              {/* 弹窗标题 */}
              <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#D0D5E2' }}>
                <div className="flex items-center gap-2">
                  <FlaskConical className="w-4 h-4" style={{ color: 'oklch(0.66 0.19 42)' }} />
                  <span className="text-sm font-bold" style={{ color: '#1A2337' }}>从产品配方库导入</span>
                  <span className="text-xs font-mono" style={{ color: '#5A6780' }}>— 选择配方后自动计算建议报价</span>
                </div>
                <button onClick={() => setShowRecipePicker(false)} style={{ color: '#5A6780' }} className="hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* 搜索框 + 刷新按钮 */}
              <div className="px-5 py-3 border-b" style={{ borderColor: '#D0D5E2' }}>
                <div className="flex gap-2 items-center">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: '#5A6780' }} />
                    <input
                      type="search"
                      value={recipeSearch}
                      onChange={e => setRecipeSearch(e.target.value)}
                      placeholder="搜索产品编号或名称…"
                      className="w-full pl-9 pr-4 py-2 rounded-sm text-sm outline-none"
                      style={{ background: '#F7F8FA', border: '1px solid #D0D5E2', color: '#1A2337' }}
                      autoFocus
                    />
                  </div>
                  {/* 刷新按钮：重新同步所有 BOM 配方信息 */}
                  <button
                    onClick={() => fetchRecipes()}
                    disabled={loadingRecipes}
                    title="重新同步配方库信息"
                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-sm flex-shrink-0 transition-all hover:opacity-80 disabled:opacity-50"
                    style={{ background: '#F0F2F5', border: '1px solid #D0D5E2', color: '#1A2337' }}
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${loadingRecipes ? 'animate-spin' : ''}`} style={{ color: 'oklch(0.66 0.19 42)' }} />
                    同步
                  </button>
                </div>
              </div>

              {/* 配方列表 */}
              <div className="flex-1 overflow-y-auto">
                {loadingRecipes ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'oklch(0.66 0.19 42)' }} />
                    <span className="ml-2 text-sm" style={{ color: '#5A6780' }}>加载配方中…</span>
                  </div>
                ) : (() => {
                  const filtered = recipes.filter(r => {
                    const q = recipeSearch.toLowerCase();
                    return !q || r.product_code.toLowerCase().includes(q) || r.product_name.toLowerCase().includes(q) || r.material_type.toLowerCase().includes(q);
                  });
                  return filtered.length === 0 ? (
                    <div className="py-12 text-center" style={{ color: '#5A6780' }}>
                      <Package2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">未找到匹配配方</p>
                      <p className="text-xs mt-1">请先在「BOM&动态成本核算」的「产品配方库」中创建配方</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr style={{ background: '#F0F2F5', borderBottom: '1px solid #D0D5E2' }}>
                          {['产品编号', '产品名称 / 密度', '材质', '技术特性', '操作'].map(h => (
                            <th key={h} className="text-left px-4 py-2.5 text-xs font-mono font-bold tracking-widest uppercase" style={{ color: '#1A2337' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((r, i) => {
                          // 配方选择弹窗：技术特性列直接从 RecipeOption 字段用 SPEC_TAG_CFG 渲染，与配方库完全一致
                          const specEntries: Array<[keyof RecipeOption, string]> = [
                            ['spec_temp_range',          'spec_temp_range'],
                            ['spec_acid_resistance',     'spec_acid_resistance'],
                            ['spec_alkali_resistance',   'spec_alkali_resistance'],
                            ['spec_oil_resistance',      'spec_oil_resistance'],
                            ['spec_ozone_resistance',    'spec_ozone_resistance'],
                            ['spec_compression_set',     'spec_compression_set'],
                            ['spec_tensile_strength',    'spec_tensile_strength'],
                            ['spec_elongation',          'spec_elongation'],
                            ['spec_seawater_resistance', 'spec_seawater_resistance'],
                            ['spec_compliance',          'spec_compliance'],
                            ['spec_tolerance',           'spec_tolerance'],
                            ['spec_qa_req',              'spec_qa_req'],
                          ];
                          const hasSpec = specEntries.some(([k]) => !!r[k]);
                          return (
                          <tr
                            key={r.id}
                            style={{ background: i % 2 === 0 ? '#FFFFFF' : '#F7F8FA', borderBottom: '1px solid #D0D5E2' }}
                            className="hover:bg-primary/5 transition-colors"
                          >
                            <td className="px-4 py-3">
                              <span className="font-mono text-xs font-bold px-1.5 py-0.5 rounded-sm" style={{ background: 'rgba(249,115,22,0.12)', color: 'oklch(0.66 0.19 42)', border: '1px solid rgba(249,115,22,0.2)' }}>
                                {r.product_code}
                              </span>
                            </td>
                            <td className="px-4 py-3" style={{ color: '#1A2337' }}>
                              <div className="font-medium">{r.product_name}</div>
                              {r.spec_thickness && <div className="text-xs text-[#5A6780]">密度: {r.spec_thickness}</div>}
                              {r.spec_hardness && <div className="text-xs text-[#5A6780]">硬度: {r.spec_hardness}</div>}
                            </td>
                            <td className="px-4 py-3">
                              {(() => {
                                const c = MATERIAL_TYPE_COLOR[r.material_type] ?? '#8A9BB5';
                                return (
                                  <span className="text-xs font-bold px-1.5 py-0.5 rounded-sm"
                                    style={{ background: c + '22', color: c, border: `1px solid ${c}44` }}>
                                    {r.material_type}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-4 py-3" style={{ maxWidth: 240 }}>
                              {hasSpec ? (
                                <div className="flex flex-wrap gap-1">
                                  {r.spec_thickness && (
                                    <span style={{ background: 'rgba(180,83,9,0.10)', color: '#B45309', border: '1px solid #B4530955', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                      密度: {r.spec_thickness}
                                    </span>
                                  )}
                                  {r.spec_hardness && (
                                    <span style={{ background: 'rgba(29,78,216,0.09)', color: '#1D4ED8', border: '1px solid #1D4ED855', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                      硬度: {r.spec_hardness}
                                    </span>
                                  )}
                                  {specEntries.map(([k, cfgKey]) => {
                                    const val = r[k];
                                    if (!val) return null;
                                    const cfg = SPEC_TAG_CFG[cfgKey as string];
                                    return (
                                      <span key={k} style={{
                                        background: cfg?.bg ?? 'rgba(55,48,163,0.09)',
                                        color: cfg?.color ?? '#3730A3',
                                        border: `1px solid ${cfg?.color ? cfg.color + '55' : '#C7D2FE'}`,
                                        borderRadius: 4, padding: '1px 5px',
                                        fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap',
                                      }}>{cfg?.label ?? cfgKey}: {val}</span>
                                    );
                                  })}
                                </div>
                              ) : (
                                <span className="text-xs text-[#5A6780]">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => handleImportRecipe(r)}
                                disabled={importingId === r.id}
                                className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm hover:opacity-90 disabled:opacity-50 transition-opacity"
                                style={{ background: 'oklch(0.66 0.19 42)', color: '#fff' }}
                              >
                                {importingId === r.id
                                  ? <><Loader2 className="w-3 h-3 animate-spin" />计算中…</>
                                  : <>导入</>}
                              </button>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                })()}
              </div>

              {/* 弹窗底部提示 */}
              <div className="px-5 py-3 border-t text-xs" style={{ borderColor: '#D0D5E2', color: '#5A6780' }}>
                导入后系统自动计算建议报价（原料成本 + 工艺费 × 毛利35%），可在编辑行中调整运费和折扣后再保存。
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* 删除确认弹窗 */}
      <AnimatePresence>
        {deleteId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setDeleteId(null)} className="absolute inset-0 bg-black/60" />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="relative card-industrial p-6 max-w-sm w-full shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="w-6 h-6 text-destructive flex-shrink-0" />
                <h3 className="font-bold">确认删除？</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                删除后无法恢复，且已关联此材料的报价单数据将受影响。
              </p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteId(null)}
                  className="flex-1 py-2 text-sm border border-border rounded-sm text-muted-foreground hover:text-foreground transition-colors">
                  取消
                </button>
                <button onClick={() => handleDelete(deleteId)}
                  className="flex-1 py-2 text-sm font-bold rounded-sm bg-destructive text-destructive-foreground hover:opacity-90 transition-opacity">
                  确认删除
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-sm shadow-xl text-sm font-medium border ${
              toast.type === 'success'
                ? 'bg-green-50 border-green-500/30 text-green-700'
                : 'bg-red-50 border-red-300 text-red-700'
            }`}>
            {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ══════════════════════════════════════════════════
// BOMPageView — 可嵌入 AdminDashboard 的导出版本
// （无顶栏，供 Dashboard 内嵌使用，永久稳定）
// ══════════════════════════════════════════════════
export function BOMPageView() {
  return <AdminBOMContent />;
}

// ══════════════════════════════════════════════════
// AdminBOM — 独立路由页面 /admin/bom（含顶栏）
// ══════════════════════════════════════════════════
export default function AdminBOM() {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) setUserEmail(data.user.email ?? '');
    });
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="flex items-center justify-between px-6 h-14">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-primary rounded-sm flex items-center justify-center">
              <span className="text-primary-foreground font-black text-xs">P</span>
            </div>
            <span className="font-black tracking-wider text-sm uppercase">PolyPrime</span>
            <span className="hidden sm:block text-muted-foreground text-xs font-mono mx-2">/</span>
            <span className="hidden sm:block text-xs font-mono text-muted-foreground uppercase tracking-widest">商务报价测算</span>
          </div>
          <div className="flex items-center gap-4">
            {userEmail && <span className="hidden md:block text-xs text-muted-foreground font-mono">{userEmail}</span>}
            <button onClick={() => navigate('/admin/dashboard')} className="text-xs text-muted-foreground hover:text-foreground transition-colors">返回看板</button>
            <button onClick={async () => { await supabase.auth.signOut(); navigate('/admin/login', { replace: true }); }}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <LogOut className="w-3.5 h-3.5" /><span>退出</span>
            </button>
          </div>
        </div>
      </header>
      <AdminBOMContent />
    </div>
  );
}